import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { supabase } from "@/lib/supabase";

type OrderItem = {
  id: number;
  product_id: string;
  product_name: string;
  sku_label: string | null;
  quantity: number;
  final_price: number | null;
  subtotal: number | null;
};

type OrderRecord = {
  id: number;
  status: string | null;
  currency: string;
  subtotal_items: number | null;
  shipping_total: number | null;
  recipient: Record<string, string> | null;
  created_at: string;
  order_items: OrderItem[];
};

export default function CheckoutSuccessPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const [order, setOrder] = useState<OrderRecord | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!orderId) {
      setLoading(false);
      return;
    }
    let alive = true;
    (async () => {
      try {
        const { data, error: e } = await supabase
          .from("order_headers")
          .select("*, order_items(*)")
          .eq("id", Number(orderId))
          .maybeSingle();
        if (!alive) return;
        if (e) throw e;
        setOrder(data as OrderRecord | null);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : "Couldn't load your order");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderId]);

  return (
    <main className="min-h-screen bg-background-50 text-foreground-950">
      <Navbar />

      <section className="pt-32 md:pt-40 pb-20 md:pb-28 px-4 md:px-10 lg:px-14">
        <div className="max-w-2xl mx-auto">
          {loading && (
            <div className="text-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-foreground-600"></i>
            </div>
          )}

          {!loading && error && (
            <div className="text-center py-12">
              <p className="text-sm text-foreground-700 mb-4">{error}</p>
              <Link
                to="/#collections"
                className="inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
              >
                Back to shop
              </Link>
            </div>
          )}

          {!loading && !error && !order && (
            <div className="text-center py-12">
              <h1 className="font-heading text-2xl text-foreground-950">
                Order not found
              </h1>
              <Link
                to="/#collections"
                className="mt-5 inline-flex items-center gap-2 px-5 h-10 rounded-full bg-foreground-950 text-background-50 text-sm cursor-pointer whitespace-nowrap"
              >
                Back to shop
              </Link>
            </div>
          )}

          {!loading && order && (
            <>
              <div className="text-center">
                <h1 className="font-heading text-3xl md:text-4xl text-foreground-950">
                  Thanks for your order
                </h1>
                <p className="text-sm text-foreground-700 mt-2">
                  Order #{order.id}
                </p>
                {order.recipient?.email && (
                  <p className="text-sm text-foreground-600 mt-1">
                    Confirmation sent to {order.recipient.email}
                  </p>
                )}
              </div>

              <div className="mt-10 rounded-lg bg-background-100/70 border border-background-200/70 overflow-hidden">
                <ul className="divide-y divide-background-200/70">
                  {order.order_items?.map((it) => (
                    <li
                      key={it.id}
                      className="px-5 py-4 flex items-start justify-between gap-4"
                    >
                      <div>
                        <p className="text-sm font-medium text-foreground-950">
                          {it.product_name}
                        </p>
                        {it.sku_label && (
                          <p className="text-xs text-foreground-600 mt-0.5">
                            {it.sku_label}
                          </p>
                        )}
                        <p className="text-xs text-foreground-700 mt-1">
                          Qty {it.quantity}
                        </p>
                      </div>
                      <p className="text-sm font-medium text-foreground-950">
                        ${Number(it.subtotal ?? 0).toFixed(2)}
                      </p>
                    </li>
                  ))}
                </ul>
                <div className="px-5 py-4 border-t border-background-200/70 bg-background-50 space-y-2">
                  <Row
                    label="Subtotal"
                    value={`$${Number(order.subtotal_items ?? 0).toFixed(2)}`}
                  />
                  <Row
                    label="Shipping"
                    value={
                      Number(order.shipping_total ?? 0) === 0
                        ? "Free"
                        : `$${Number(order.shipping_total ?? 0).toFixed(2)}`
                    }
                  />
                  <div className="pt-2 border-t border-background-200/70 flex items-baseline justify-between">
                    <span className="text-sm font-medium text-foreground-950">Total</span>
                    <span className="text-lg font-medium text-foreground-950">
                      ${totalOf(order).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {order.recipient && (order.recipient.address || order.recipient.city) && (
                <div className="mt-6 rounded-lg bg-background-100/70 border border-background-200/70 p-5">
                  <p className="text-xs text-foreground-700 mb-2">Shipping to</p>
                  <div className="text-sm text-foreground-900 leading-relaxed">
                    {order.recipient.name && <p>{order.recipient.name}</p>}
                    {order.recipient.address && <p>{order.recipient.address}</p>}
                    <p>
                      {[order.recipient.city, order.recipient.state, order.recipient.zip]
                        .filter(Boolean)
                        .join(", ")}
                    </p>
                    {order.recipient.country && <p>{order.recipient.country}</p>}
                  </div>
                </div>
              )}

              <div className="mt-10 text-center">
                <Link
                  to="/#collections"
                  className="inline-flex items-center justify-center gap-2 px-6 h-11 rounded-full bg-foreground-950 text-background-50 text-sm font-medium hover:bg-foreground-900 cursor-pointer whitespace-nowrap"
                >
                  Continue shopping
                </Link>
              </div>
            </>
          )}
        </div>
      </section>

      <Footer />
    </main>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-foreground-700">{label}</span>
      <span className="text-foreground-950 font-medium">{value}</span>
    </div>
  );
}

function totalOf(o: OrderRecord) {
  return Number(o.subtotal_items ?? 0) + Number(o.shipping_total ?? 0);
}